import type { ReactNode } from 'react'
import { Icon, type IconName } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { useWidgetDensity } from '../grid/WidgetDensityContext'

export type WidgetLoadState = 'loading' | 'ok' | 'not_authorized' | 'error' | 'coming_soon' | 'empty'

export interface WidgetDrillAction {
  label: string
  onClick: () => void
}

/**
 * Reporting & Exports (client-side CSV of exactly this tile's loaded data) —
 * a second, independent header action from `drill`; both may render at once
 * (e.g. "View filings" + "Export CSV" on the same tile).
 */
export interface WidgetExportAction {
  label?: string
  onClick: () => void
}

export interface WidgetFrameProps {
  title: string
  icon: IconName
  state: WidgetLoadState
  message?: string | null | undefined
  onRetry?: (() => void) | undefined
  drill?: WidgetDrillAction | undefined
  exportAction?: WidgetExportAction | undefined
  /** `hero` = the compact top-row KPI treatment (`DashboardGrid`'s untitled hero section) — a thin
   * accent top border, nothing else changes (the actual size reduction comes from auto-density). */
  variant?: 'default' | 'hero'
  children?: ReactNode
}

/**
 * Every widget renders through this shell so one widget's `error`/
 * `not_authorized`/`coming_soon` state is visually contained to its own
 * card and never blanks the rest of the dashboard (AC26) — sibling widgets
 * keep rendering regardless of what this one is doing.
 *
 * **Auto-density chrome (IB-311 Slice 1):** at `compact`/`micro` (only ever true inside a
 * `DashboardGrid` cell — see `WidgetDensityContext`) the header actions collapse to icon-only
 * (accessible name preserved via `aria-label`) and the title truncates with an ellipsis instead of
 * wrapping, so a squeezed tile never overflows or crams two lines into a one-line header.
 */
export function WidgetFrame({ title, icon, state, message, onRetry, drill, exportAction, variant = 'default', children }: WidgetFrameProps) {
  const { density } = useWidgetDensity()
  const chromeCompact = density !== 'full'

  return (
    <section
      className={`iw-widget-card${variant === 'hero' ? ' iw-widget-card--hero' : ''}`}
      aria-label={title}
      data-density={density}
    >
      <div className="iw-widget-card__header">
        <span className="iw-widget-card__icon">
          <Icon name={icon} size={density === 'micro' ? 15 : 18} />
        </span>
        <h2 className="iw-widget-card__title">{title}</h2>
        {state === 'ok' && exportAction ? (
          <button
            type="button"
            className="iw-btn iw-btn--sm iw-btn--ghost"
            onClick={exportAction.onClick}
            aria-label={`Export ${title} (CSV)`}
          >
            <Icon name="download" size={14} />
            {!chromeCompact ? (exportAction.label ?? 'Export CSV') : null}
          </button>
        ) : null}
        {state === 'ok' && drill ? (
          <button
            type="button"
            className="iw-btn iw-btn--sm iw-btn--ghost"
            onClick={drill.onClick}
            aria-label={chromeCompact ? drill.label : undefined}
          >
            {!chromeCompact ? drill.label : null}
            <Icon name="chevron-right" size={14} />
          </button>
        ) : null}
      </div>

      {state === 'loading' ? (
        <div
          className="iw-skeleton"
          style={{ height: density === 'micro' ? 72 : density === 'compact' ? 110 : 160, borderRadius: 'var(--iw-radius)' }}
          aria-busy="true"
          aria-label={`${title} loading`}
        />
      ) : state === 'error' ? (
        <InlineAlert
          severity="error"
          action={
            onRetry ? (
              <button type="button" className="iw-btn iw-btn--sm" onClick={onRetry}>
                Try again
              </button>
            ) : undefined
          }
        >
          {message ?? 'This tile could not load. The rest of the dashboard is unaffected.'}
        </InlineAlert>
      ) : state === 'not_authorized' ? (
        <InlineAlert severity="info">{message ?? 'You do not have access to this tile.'}</InlineAlert>
      ) : state === 'coming_soon' ? (
        <InlineAlert severity="info">{message ?? 'Coming soon.'}</InlineAlert>
      ) : state === 'empty' ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
          {message ?? 'No data for the current filter.'}
        </p>
      ) : (
        children
      )}
    </section>
  )
}
